import React, { useState } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import UserMenu from '../../components/UserMenu';
import {
  Menu, X, LayoutDashboard, FileText, Upload, Ticket
} from 'lucide-react';

const ClientPortalLayout = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only allow client users to access this portal
  if (!currentUser || currentUser.userType !== 'client') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h1>
          <p className="text-slate-400">This portal is only accessible to client users.</p>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { name: 'Dashboard', path: `/client-portal/${currentUser.businessUnit?.identifier}`, icon: <LayoutDashboard className="w-5 h-5" /> },
    { name: 'Pages', path: `/client-portal/${currentUser.businessUnit?.identifier}/pages`, icon: <FileText className="w-5 h-5" /> },
    { name: 'File Share', path: `/client-portal/${currentUser.businessUnit?.identifier}/file-share`, icon: <Upload className="w-5 h-5" /> },
    { name: 'Tickets', path: `/client-portal/${currentUser.businessUnit?.identifier}/tickets`, icon: <Ticket className="w-5 h-5" /> }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-slate-900">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 transform 
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          <Link 
            to="/"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 rounded-md"
          >
            <span className="mr-3">←</span>
            <span>Back to HQ</span>
          </Link>
        </div>

        <nav className="mt-4">
          <div className="mb-6">
            <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              {currentUser.businessUnit?.name || 'Client Portal'}
            </h3>
            <div>
              {navigationItems.map(item => (
                <Link 
                  key={item.path} 
                  to={item.path}
                  className={`flex items-center px-4 py-2 text-sm ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-slate-100'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="mr-3">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 w-full lg:w-auto lg:ml-64">
        <header className="bg-slate-800 border-slate-700 border-b sticky top-0 z-30">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-xl font-semibold text-slate-200 ml-12 lg:ml-0">
              {currentUser.businessUnit?.name || 'Client'} Portal
            </h1>
            <UserMenu isDarkMode={true} />
          </div>
        </header>
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default ClientPortalLayout;